import { AlertTriangle, X } from 'lucide-react';
import { cn } from '@/lib/utils';

const STATUS_STYLES = {
  draft: 'bg-slate-200 text-slate-700',
  in_review: 'bg-blue-100 text-blue-800',
  scheduled: 'bg-amber-100 text-amber-800',
  published: 'bg-teal-100 text-teal-800',
  archived: 'bg-slate-100 text-slate-500',
};

export const StatusBadge = ({ status }) => (
  <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide', STATUS_STYLES[status] || STATUS_STYLES.draft)} data-testid={`status-${status}`}>
    <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />
    {(status || 'draft').replace('_', ' ')}
  </span>
);

export const SeoWarnings = ({ warnings }) => {
  if (!warnings?.length) return null;
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="seo-warnings">
      <p className="flex items-center gap-1.5 text-xs font-bold text-amber-900">
        <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" /> SEO checks
      </p>
      <ul className="mt-1.5 space-y-1">
        {warnings.map((w) => (
          <li key={w.code} className="text-xs text-amber-800">{w.message}</li>
        ))}
      </ul>
    </div>
  );
};

export const ConfirmDialog = ({ open, title, message, confirmLabel = 'Confirm', destructive, onConfirm, onCancel }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy-950/60 p-4" role="dialog" aria-modal="true" aria-labelledby="confirm-title">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-lift">
        <h2 id="confirm-title" className="text-lg font-extrabold text-navy-900">{title}</h2>
        <p className="mt-2 text-sm leading-relaxed text-slate-600">{message}</p>
        <div className="mt-6 flex justify-end gap-3">
          <button type="button" onClick={onCancel} data-testid="confirm-cancel" className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-4 py-2 text-sm font-bold text-navy-900 hover:bg-navy-50">
            <X className="h-4 w-4" aria-hidden="true" /> Cancel
          </button>
          <button
            type="button" onClick={onConfirm} data-testid="confirm-ok"
            className={cn('rounded-md px-4 py-2 text-sm font-bold text-white', destructive ? 'bg-red-700 hover:bg-red-600' : 'bg-navy-900 hover:bg-navy-700')}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export const Pagination = ({ page, perPage, total, onPage }) => {
  const pages = Math.max(1, Math.ceil(total / perPage));
  if (pages <= 1) return null;
  return (
    <nav className="mt-4 flex items-center justify-between text-sm" aria-label="Pagination">
      <p className="text-slate-500">Page {page} of {pages} · {total} records</p>
      <div className="flex gap-2">
        <button type="button" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="page-prev" className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-bold text-navy-900 disabled:opacity-40">
          Previous
        </button>
        <button type="button" disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid="page-next" className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-bold text-navy-900 disabled:opacity-40">
          Next
        </button>
      </div>
    </nav>
  );
};
